import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Download,
  Eye,
  Trash2,
  X,
  Check,
  Loader2,
  RefreshCcw,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";

import { adminAuthHeader } from "../../lib/adminAuth";
import SendReviewRequestButton from "./SendReviewRequestButton";
import FraudModal from "./FraudModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => adminAuthHeader();

export default function PatientsTab() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({ total: 0, items: [] });
  const [filters, setFilters] = useState({
    search: "",
    country: "",
    condition: "",
    unsubscribed: "",
  });
  const [options, setOptions] = useState({ countries: [], conditions: [] });
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const [fraudTarget, setFraudTarget] = useState(null);
  const limit = 25;

  const fetchOptions = async () => {
    try {
      const r = await axios.get(`${API}/patients/form-options`);
      setOptions({
        countries: r.data.countries || [],
        conditions: r.data.conditions || [],
      });
    } catch {
      /* form options endpoint may not be reachable, that's fine */
    }
  };

  const fetchPatients = async (overrideFilters, overridePage) => {
    setLoading(true);
    try {
      const f = overrideFilters ?? filters;
      const p = overridePage ?? page;
      const params = { limit, offset: p * limit };
      if (f.search) params.search = f.search;
      if (f.country) params.country = f.country;
      if (f.condition) params.condition = f.condition;
      if (f.unsubscribed === "yes") params.unsubscribed = true;
      if (f.unsubscribed === "no") params.unsubscribed = false;

      const r = await axios.get(`${API}/admin/patients`, {
        headers: getAuthHeaders(),
        params,
      });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to fetch patients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
    fetchPatients({ search: "", country: "", condition: "", unsubscribed: "" }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  const applyFilters = () => {
    setPage(0);
    fetchPatients(filters, 0);
  };

  const resetFilters = () => {
    const empty = { search: "", country: "", condition: "", unsubscribed: "" };
    setFilters(empty);
    setPage(0);
    fetchPatients(empty, 0);
  };

  const goToPage = (p) => {
    setPage(p);
    fetchPatients(filters, p);
  };

  const exportCsv = async () => {
    setBusy(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.country) params.country = filters.country;
      if (filters.condition) params.condition = filters.condition;
      if (filters.unsubscribed === "yes") params.unsubscribed = true;
      if (filters.unsubscribed === "no") params.unsubscribed = false;
      const r = await axios.get(`${API}/admin/patients/export`, {
        headers: getAuthHeaders(),
        responseType: "blob",
        params,
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `meditalk-patients-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export ready.");
    } catch {
      toast.error("Export failed.");
    } finally {
      setBusy(false);
    }
  };

  const deletePatient = async (id) => {
    if (!window.confirm("Delete this patient permanently? This cannot be undone."))
      return;
    try {
      await axios.delete(`${API}/admin/patients/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Patient deleted.");
      setSelected(null);
      fetchPatients(filters, page);
    } catch {
      toast.error("Failed to delete patient.");
    }
  };

  const updatePatient = async (id, payload) => {
    try {
      const r = await axios.put(
        `${API}/admin/patients/${id}`,
        payload,
        { headers: getAuthHeaders() },
      );
      toast.success("Patient updated.");
      setSelected(r.data);
      fetchPatients(filters, page);
    } catch {
      toast.error("Failed to update.");
    }
  };

  const markFraud = async (id, reason) => {
    try {
      await axios.post(
        `${API}/admin/patients/${id}/mark-fraud`,
        { reason },
        { headers: getAuthHeaders() },
      );
      toast.success("Marked as fraudulent.");
      setFraudTarget(null);
      fetchPatients(filters, page);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to mark.");
    }
  };

  const unmarkFraud = async (id) => {
    if (!window.confirm("Remove the fraudulent flag from this patient?"))
      return;
    try {
      await axios.post(
        `${API}/admin/patients/${id}/unmark-fraud`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success("Fraudulent flag removed.");
      fetchPatients(filters, page);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to unmark.");
    }
  };

  const clearSuspicion = async (id) => {
    try {
      await axios.post(
        `${API}/admin/patients/${id}/clear-suspicion`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success("Suspicion cleared.");
      fetchPatients(filters, page);
    } catch {
      toast.error("Failed to clear suspicion.");
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-['Outfit'] font-bold text-[var(--charcoal)]">
            Patients
          </h1>
          <p className="text-sm text-[var(--olive)] mt-1">
            {data.total.toLocaleString()} total · showing {data.items.length}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={busy}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50"
            data-testid="export-patients-csv"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative lg:col-span-2">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--olive)]"
            />
            <input
              data-testid="patients-search"
              className="w-full h-10 pl-9 pr-4 rounded-lg border border-[var(--sage)]/20 text-[var(--charcoal)]"
              placeholder="Search name, email, postal, legacy id"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            />
          </div>
          <select
            data-testid="patients-country"
            className="h-10 px-3 rounded-lg border border-[var(--sage)]/20 text-[var(--charcoal)] bg-white"
            value={filters.country}
            onChange={(e) => setFilters({ ...filters, country: e.target.value })}
          >
            <option value="">All countries</option>
            {options.countries.map((c) => (
              <option key={c.code} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            data-testid="patients-condition"
            className="h-10 px-3 rounded-lg border border-[var(--sage)]/20 text-[var(--charcoal)] bg-white"
            value={filters.condition}
            onChange={(e) => setFilters({ ...filters, condition: e.target.value })}
          >
            <option value="">All conditions</option>
            {options.conditions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            data-testid="patients-status"
            className="h-10 px-3 rounded-lg border border-[var(--sage)]/20 text-[var(--charcoal)] bg-white"
            value={filters.unsubscribed}
            onChange={(e) => setFilters({ ...filters, unsubscribed: e.target.value })}
          >
            <option value="">All statuses</option>
            <option value="no">Active (subscribed)</option>
            <option value="yes">Unsubscribed</option>
          </select>
        </div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={applyFilters}
            className="btn-primary"
            data-testid="apply-filters"
          >
            Apply
          </button>
          <button
            onClick={resetFilters}
            className="btn-secondary flex items-center gap-2"
            data-testid="reset-filters"
          >
            <RefreshCcw size={14} />
            Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="admin-table" data-testid="patients-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Country</th>
                <th>Conditions</th>
                <th>Source</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-[var(--olive)]">
                    <Loader2 className="animate-spin inline-block mr-2" size={18} />
                    Loading…
                  </td>
                </tr>
              ) : data.items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-[var(--olive)]">
                    No patients match these filters.
                  </td>
                </tr>
              ) : (
                data.items.map((p) => (
                  <tr
                    key={p.id}
                    className={
                      p.is_fraudulent
                        ? "bg-red-50/70"
                        : p.suspected_same_source
                        ? "bg-amber-50/60"
                        : ""
                    }
                    data-testid={`patient-row-${p.id}`}
                  >
                    <td className="font-medium text-[var(--charcoal)]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>
                          {p.first_name} {p.last_name}
                        </span>
                        {p.is_fraudulent && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white cursor-help"
                            title={
                              p.fraud_reason
                                ? `${p.fraud_reason}${
                                    p.fraud_marked_by
                                      ? ` — by ${p.fraud_marked_by}`
                                      : ""
                                  }`
                                : "Marked as fraudulent"
                            }
                            data-testid={`fraud-badge-${p.id}`}
                          >
                            <ShieldAlert size={11} />
                            Fraud
                          </span>
                        )}
                        {!p.is_fraudulent && p.suspected_same_source && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white cursor-help"
                            title={
                              p.suspected_source_reason ||
                              "Same submitter fingerprint as a fraud row"
                            }
                            data-testid={`suspicion-badge-${p.id}`}
                          >
                            Same source
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-[var(--olive)] max-w-[220px] truncate">
                      {p.email}
                    </td>
                    <td>{p.country}</td>
                    <td className="max-w-[260px] truncate text-[var(--olive)]">
                      {(p.conditions || [])
                        .map((c) =>
                          c.condition_label === "Other" && c.specify
                            ? c.specify
                            : c.condition_label,
                        )
                        .join(", ")}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          p.source === "legacy_import"
                            ? "badge-sunny"
                            : "badge-sage"
                        }`}
                      >
                        {p.source === "legacy_import" ? "Legacy" : "Website"}
                      </span>
                    </td>
                    <td>
                      {p.unsubscribed ? (
                        <span className="text-red-600 text-sm">
                          Unsubscribed
                        </span>
                      ) : (
                        <span className="text-[var(--success)] flex items-center gap-1 text-sm">
                          <Check size={14} /> Active
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelected(p)}
                          className="p-2 hover:bg-[var(--sage)]/10 rounded-lg transition-colors"
                          data-testid={`view-patient-${p.id}`}
                          title="View"
                        >
                          <Eye size={16} className="text-[var(--sage)]" />
                        </button>
                        {p.is_fraudulent ? (
                          <button
                            onClick={() => unmarkFraud(p.id)}
                            className="p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                            data-testid={`unmark-fraud-${p.id}`}
                            title="Remove fraud flag"
                          >
                            <ShieldCheck size={16} className="text-emerald-600" />
                          </button>
                        ) : (
                          <button
                            onClick={() =>
                              setFraudTarget({ ...p, _type: "patient" })
                            }
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            data-testid={`mark-fraud-${p.id}`}
                            title="Mark as fraudulent"
                          >
                            <ShieldAlert size={16} className="text-red-600" />
                          </button>
                        )}
                        {!p.is_fraudulent && p.suspected_same_source && (
                          <button
                            onClick={() => clearSuspicion(p.id)}
                            className="p-2 hover:bg-amber-50 rounded-lg transition-colors"
                            data-testid={`clear-suspicion-${p.id}`}
                            title="Clear same-source suspicion"
                          >
                            <X size={16} className="text-amber-600" />
                          </button>
                        )}
                        <button
                          onClick={() => deletePatient(p.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          data-testid={`delete-patient-${p.id}`}
                          title="Delete"
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data.total > limit && (
          <div className="flex items-center justify-between p-4 border-t border-[var(--sage)]/10">
            <span className="text-sm text-[var(--olive)]">
              Page {page + 1} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page === 0}
                onClick={() => goToPage(page - 1)}
                className="btn-secondary disabled:opacity-50 px-4 py-2 text-sm"
              >
                Previous
              </button>
              <button
                disabled={page + 1 >= totalPages}
                onClick={() => goToPage(page + 1)}
                className="btn-secondary disabled:opacity-50 px-4 py-2 text-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <PatientDetail
          patient={selected}
          onClose={() => setSelected(null)}
          onSave={updatePatient}
          onDelete={deletePatient}
        />
      )}

      {fraudTarget && (
        <FraudModal
          target={fraudTarget}
          targetType="patient"
          onCancel={() => setFraudTarget(null)}
          onConfirm={(reason) => markFraud(fraudTarget.id, reason)}
        />
      )}
    </>
  );
}

function PatientDetail({ patient, onClose, onSave, onDelete }) {
  const [notes, setNotes] = useState(patient.notes || "");
  const [unsubscribed, setUnsubscribed] = useState(!!patient.unsubscribed);
  const dirty = useMemo(
    () =>
      notes !== (patient.notes || "") ||
      unsubscribed !== !!patient.unsubscribed,
    [notes, unsubscribed, patient],
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="admin-modal-panel bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="patient-detail-modal"
      >
        <div className="p-6 border-b border-[var(--sage)]/10 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-['Outfit'] font-semibold text-[var(--charcoal)]">
              {patient.first_name} {patient.last_name}
            </h2>
            <p className="text-sm text-[var(--olive)]">
              {patient.email} · {patient.country}
              {patient.legacy_id && ` · Legacy #${patient.legacy_id}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[var(--sage)]/10 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <Info label="Phone">
              {patient.phone_prefix} {patient.phone_number}
            </Info>
            <Info label="Date of birth">{patient.date_of_birth}</Info>
            <Info label="Gender">{patient.gender}</Info>
            <Info label="Ethnicity">
              {patient.ethnicity}
              {patient.ethnicity_specify && `: ${patient.ethnicity_specify}`}
            </Info>
            <Info label="Postal / ZIP">{patient.postal_code}</Info>
            <Info label="Source">{patient.source}</Info>
            <Info label="Registered">
              {new Date(patient.created_at).toLocaleString()}
            </Info>
            <Info label="Last updated">
              {new Date(patient.updated_at).toLocaleString()}
            </Info>
          </div>

          <div>
            <h3 className="font-semibold text-[var(--charcoal)] mb-2">
              Conditions ({patient.conditions?.length || 0})
            </h3>
            {patient.conditions?.length ? (
              <div className="space-y-2">
                {patient.conditions.map((c, i) => (
                  <div
                    key={c.id || i}
                    className="rounded-lg border border-[var(--sage)]/15 p-3 text-sm"
                  >
                    <div className="font-medium text-[var(--charcoal)]">
                      {c.condition_label}
                      {c.specify ? `: ${c.specify}` : ""}
                    </div>
                    <div className="text-[var(--olive)] mt-1 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                      {c.diagnosis_date && (
                        <span>
                          <strong>Diagnosed:</strong> {c.diagnosis_date}
                        </span>
                      )}
                      {c.severity && (
                        <span>
                          <strong>Severity:</strong> {c.severity}
                        </span>
                      )}
                      {c.treatment && (
                        <span className="md:col-span-1">
                          <strong>Treatment:</strong> {c.treatment}
                        </span>
                      )}
                      {c.other_info && (
                        <span className="md:col-span-3">
                          <strong>Other:</strong> {c.other_info}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[var(--olive)] text-sm">No conditions on file.</p>
            )}
          </div>

          <div>
            <h3 className="font-semibold text-[var(--charcoal)] mb-2">Consents</h3>
            <ul className="text-sm text-[var(--olive)] space-y-1">
              {Object.entries(patient.consents || {}).map(([k, v]) => (
                <li key={k} className="flex items-center gap-2">
                  {v ? (
                    <Check size={14} className="text-[var(--success)]" />
                  ) : (
                    <X size={14} className="text-red-600" />
                  )}
                  {k.replace(/_/g, " ")}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--charcoal)] mb-1">
              Admin notes
            </label>
            <textarea
              data-testid="patient-notes"
              className="w-full h-24 px-3 py-2 rounded-lg border border-[var(--sage)]/20 text-[var(--charcoal)] resize-none"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes about this patient (not shared)"
            />
          </div>

          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              data-testid="patient-unsubscribed"
              type="checkbox"
              className="h-4 w-4"
              checked={unsubscribed}
              onChange={(e) => setUnsubscribed(e.target.checked)}
            />
            <span className="text-sm text-[var(--charcoal)]">
              Unsubscribed (do not contact)
            </span>
          </label>
        </div>

        <div className="p-6 border-t border-[var(--sage)]/10 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => onDelete(patient.id)}
              className="text-sm text-red-600 hover:underline inline-flex items-center gap-1"
              data-testid="modal-delete-patient"
            >
              <Trash2 size={14} />
              Delete patient
            </button>
            <SendReviewRequestButton
              audience="patient"
              record_id={patient.id}
              disabled={!patient.email}
            />
          </div>
          <div className="flex gap-2 sm:justify-end">
            <button onClick={onClose} className="btn-secondary">
              Close
            </button>
            <button
              onClick={() => onSave(patient.id, { notes, unsubscribed })}
              disabled={!dirty}
              className="btn-primary disabled:opacity-50"
              data-testid="save-patient-btn"
            >
              Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-[var(--olive)]">
        {label}
      </div>
      <div className="text-[var(--charcoal)]">{children || "-"}</div>
    </div>
  );
}
